import { useState } from 'react';
import { ArrowRight, BarChart3 } from 'lucide-react';
import { useGetBuildList } from '@/components/smartai/api/getBuildList';
import { useCompareBuildDiff } from '@/components/smartai/api/compareBuildDiff';
import type { CompareResponse } from '@/components/smartai/api/compareBuildDiff';
import type {
  Build,
  BuildListResponse,
} from '@/components/smartai/api/getBuildList';
import {
  useIngestTestCasesMutation,
  useIngestJiraTestCasesMutation,
  useGenerateMappingMutation,
  type GapAnalysisResponse,
} from '@/components/smartai/api/testIntelligence';
import FileLevelChange from './FileLevelChange';
import MapTestCasesModal from './MapTestCasesModal';
import TestMappingView from './TestMappingView';
import TestOptimizationPanel from '../TestOptimization/TestOptimizationPanel';
import { useSelector } from 'react-redux';
import { RootState } from '@/state/store';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import { formLabelClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import {
  inferScmProviderFromRepoUrl,
  type ScmProviderId,
} from '@/types/scm-provider';
import { Combobox } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  ActivityTimeline,
  BuildDetailsPanel,
  BuildDiffCardHeader,
  buildDiffCardClass,
  buildDiffPageClass,
  FilterPill,
  ModeToggle,
  type TimelineEventItem,
} from './build-diff-layout';
import BuildAnalysisSkeleton from './BuildAnalysisSkeleton';

const TODAY = new Date().toISOString();

const formatBranch = (branch: string | undefined): string =>
  (branch || 'main').replace(/^refs\/heads\//, '');

const formatBuildLabel = (build: Build): string => {
  const status =
    build.result === 'succeeded'
      ? 'Passed'
      : build.result === 'partiallySucceeded'
        ? 'Partially succeeded'
        : build.result === 'canceled'
          ? 'Canceled'
          : build.result === 'failed'
            ? 'Failed'
            : build.result;
  return `#${build.buildNumber} · ${formatBranch(build.sourceBranch)} · ${status}`;
};

const getRelativeTime = (timestamp: string): string => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffMs = now.getTime() - time.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;

  return time.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const RESULT_TABS = [
  {id: 'code-diff' as const, label: 'Comparison'},
  {id: 'test-intelligence' as const, label: 'Test Intelligence'},
  {id: 'test-optimization' as const, label: 'Test Optimization'},
];

const ComponentBuildDiff = () => {
  const { ready, isLoading, notFound } = useSelectedComponent();
  const userSelectedComponent = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );

  const scmProvider: ScmProviderId =
    userSelectedComponent.provider ??
    inferScmProviderFromRepoUrl(userSelectedComponent.repoUrl ?? '');

  const repositoryRef =
    scmProvider === 'github'
      ? userSelectedComponent.repoName || userSelectedComponent.repoId
      : userSelectedComponent.repoId;

  const { data: buildListData, isPending: isBuildsLoading } = useGetBuildList(
    {
      organization: userSelectedComponent?.orgName ?? '',
      project: userSelectedComponent?.projectName ?? '',
      repositoryId: repositoryRef ?? '',
      repository: userSelectedComponent.repoName || repositoryRef,
      branchName: userSelectedComponent.branchName,
      workflowId: userSelectedComponent.pipelineId || undefined,
      minTime: userSelectedComponent.onboardedAt ?? '',
      maxTime: TODAY,
      provider: scmProvider,
      repoUrl: userSelectedComponent.repoUrl,
    },
    userSelectedComponent.pat,
  );

  const { mutate: compareBuildDiff, isPending: isComparing } =
    useCompareBuildDiff(userSelectedComponent.pat, scmProvider);

  const buildList: Build[] = (
    (buildListData as BuildListResponse)?.builds || []
  ).sort(
    (a: Build, b: Build) =>
      new Date(b.queueTime).getTime() - new Date(a.queueTime).getTime(),
  );

  const [buildBOptions, setBuildBOptions] = useState<Build[]>([]);
  const [selectedBuildA, setSelectedBuildA] = useState('');
  const [selectedBuildB, setSelectedBuildB] = useState('');
  const [dateSwitch, setDateSwitch] = useState(false);
  const [dateA, setDateA] = useState('');
  const [dateB, setDateB] = useState('');
  const [diffData, setDiffData] = useState<CompareResponse | null>(null);

  const [buildAObj, setBuildAObj] = useState<Build>();
  const [buildBObj, setBuildBObj] = useState<Build>();

  const [selectTab, setSelectTab] = useState<
     'code-diff'| 'test-intelligence'| 'test-optimization'
  >('code-diff');
  const [showMapTestCases, setShowMapTestCases] = useState(false);
  const [thresholdValue, setThresholdValue] = useState(75);
  const [ingestionId, setIngestionId] = useState('');
  const [generatedId, setGeneratedId] = useState<string | null>(null);
  const [showTestMethodMapping, setShowTestMethodMapping] = useState(false);
  const [gapAnalysis, setGapAnalysis] = useState<GapAnalysisResponse | null>(
    null,
  );
  const [mapTestPatToken, setMapTestPatToken] = useState('');
  const [isMappingLoading, setIsMappingLoading] = useState(false);

  const { mutateAsync: ingestTestCases } = useIngestTestCasesMutation();
  const { mutateAsync: ingestJiraTestCases } = useIngestJiraTestCasesMutation();
  const { mutateAsync: generateMapping } = useGenerateMappingMutation();

  const handleTestOptimization = () => {
    const sid =
      diffData?.scanB?.scanId?.trim() || userSelectedComponent.scanId?.trim();
    if (!sid) {
      window.alert(
        'No scan found for Build B. Run build processing for this build, or complete component onboarding.',
      );
      return;
    }
    setSelectTab('test-optimization');
  };

  const handleMethodMappingView = async ({
    planId,
    suiteId,
    patToken,
  }: {
    planId: string;
    suiteId: string;
    patToken: string;
  }) => {
    setIsMappingLoading(true);
    try {
      const { ingestionId: iId } = await ingestTestCases({
        organization: userSelectedComponent.orgName,
        project: userSelectedComponent.projectName,
        planId,
        suiteIds: [suiteId],
        _pat: patToken,
      });
      setIngestionId(iId);
      const { generationId } = await generateMapping({
        ingestionId: iId,
        diffResult: { ...diffData!, additionalProp1: {} },
        repositoryUrl: 'string',
        oldCommit: 'string',
        newCommit: 'string',
        buildANumber: 'string',
        buildBNumber: 'string',
        extraContext: 'string',
        _pat: patToken,
      });
      setGeneratedId(generationId);
      setShowTestMethodMapping(true);
      setGapAnalysis(null);
      setShowMapTestCases(false);
    } finally {
      setIsMappingLoading(false);
    }
  };

  const handleJiraMapping = async ({
    organization,
    project,
    jiraEmail,
    jiraApiToken,
  }: {
    organization: string;
    project: string;
    jiraEmail: string;
    jiraApiToken: string;
  }) => {
    setIsMappingLoading(true);
    try {
      const { ingestionId: iId } = await ingestJiraTestCases({
        organization,
        project,
        jiraEmail,
        jiraApiToken,
      });
      setIngestionId(iId);
      const { generationId } = await generateMapping({
        ingestionId: iId,
        diffResult: { ...diffData!, additionalProp1: {} },
        repositoryUrl: userSelectedComponent.repoUrl,
        oldCommit: 'string',
        newCommit: 'string',
        buildANumber: 'string',
        buildBNumber: 'string',
        extraContext: 'string',
        _pat: userSelectedComponent.pat || jiraApiToken,
      });
      setGeneratedId(generationId);
      setShowTestMethodMapping(true);
      setGapAnalysis(null);
      setShowMapTestCases(false);
    } finally {
      setIsMappingLoading(false);
    }
  };

  const handleBuildAChange = (val: string | number | (string | number)[]) => {
    const raw = Array.isArray(val) ? val[0] : val;
    const id = Number(raw);
    const buildA = buildList.find(b => b.buildId === id);

    const compareDate = new Date(buildA?.queueTime || '');
    setBuildBOptions(
      buildList
        .filter(b => new Date(b.queueTime) > compareDate)
        .sort(
          (a, b) =>
            new Date(b.queueTime).getTime() - new Date(a.queueTime).getTime(),
        ),
    );
    setBuildAObj(buildA);
    setSelectedBuildA(String(raw));
    setSelectedBuildB('');
    setBuildBObj(undefined);
  };

  const handleBuildBChange = (val: string | number | (string | number)[]) => {
    const raw = Array.isArray(val) ? val[0] : val;
    const id = Number(raw);
    const buildB = buildList.find(b => b.buildId === id);
    setBuildBObj(buildB);
    setSelectedBuildB(String(raw));
  };

  const handleGenerateReport = () => {
    if (!dateSwitch) {
      const a = buildAObj;
      const b = buildBObj;
      if (!a || !b) return;

      compareBuildDiff(
        {
          repositoryUrl: a.repositoryUrl,
          repoId: userSelectedComponent.dbRepoId || undefined,
          provider: scmProvider,
          buildA: {
            ...a,
            branch: a.sourceBranch,
            commitHash: a.commitId,
            buildId: a.buildId,
          },
          buildB: {
            ...b,
            branch: b.sourceBranch,
            commitHash: b.commitId,
            buildId: b.buildId,
          },
        },
        {
          onSuccess: (res: CompareResponse) => setDiffData(res),
        },
      );
    } else {
      const startDate = new Date(dateA);
      const endDate = new Date(dateB);
      endDate.setHours(23, 59, 59, 999);

      const filtered = buildList
        .filter(b => {
          const d = new Date(b.queueTime);
          return d >= startDate && d <= endDate;
        })
        .sort(
          (a, b) =>
            new Date(a.queueTime).getTime() - new Date(b.queueTime).getTime(),
        );

      const first = filtered[0];
      const last = filtered[filtered.length - 1];
      if (!first || !last) return;

      compareBuildDiff(
        {
          repositoryUrl: first.repositoryUrl,
          repoId: userSelectedComponent.dbRepoId || undefined,
          provider: scmProvider,
          buildA: {
            ...first,
            branch: first.sourceBranch,
            commitHash: first.commitId,
            buildId: first.buildId,
          },
          buildB: {
            ...last,
            branch: last.sourceBranch,
            commitHash: last.commitId,
            buildId: last.buildId,
          },
        },
        {
          onSuccess: (res: CompareResponse) => setDiffData(res),
        },
      );
    }
  };

  const timelineEvents: TimelineEventItem[] = buildList.map(build => ({
    id: build.buildId.toString(),
    title: `Build #${build.buildNumber}`,
    description: formatBranch(build.sourceBranch),
    time:
      !build.finishTime || build.finishTime === 'N/A'
        ? ''
        : getRelativeTime(build.finishTime),
    build,
    isSelected:
      build.buildId.toString() === selectedBuildA ||
      build.buildId.toString() === selectedBuildB,
  }));

  if (isLoading || (!ready && !notFound) || isBuildsLoading) {
    return <BuildAnalysisSkeleton />;
  }
  if (notFound) return <ComponentNotFound />;

  return (
    <div className={buildDiffPageClass}>
      <OnboardingPageHeader
        eyebrow="Code Analysis"
        title="Build Analysis"
        subtitle="Compare workflow runs, track repository activity, and review code changes between builds."
      />

      <OnboardingProjectHeader
        org={userSelectedComponent.orgName}
        project={userSelectedComponent.projectName || '—'}
        repoName={userSelectedComponent.repoName || '—'}
        branchName={formatBranch(userSelectedComponent.branchName) || '—'}
      />

      <div className="mb-5 grid grid-cols-1 items-start gap-4">
        <section className={buildDiffCardClass} aria-label="Compare builds">
          <BuildDiffCardHeader
            title="Compare builds"
            subtitle="Select two builds or a date range to generate a diff report."
            action={
              <ModeToggle byDate={dateSwitch} onChange={setDateSwitch} />
            }
          />

          {!dateSwitch ? (
            <div className="flex flex-col gap-4">
              <div className="hidden grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-5 gap-y-2 min-[769px]:grid">
                <label className={formLabelClass} htmlFor="build-a-select">
                  Build A (from)
                </label>
                <span aria-hidden />
                <label className={formLabelClass} htmlFor="build-b-select">
                  Build B (to)
                </label>
                <Combobox
                  id="build-a-select"
                  data={buildList.map(b => ({
                    value: String(b.buildId),
                    label: formatBuildLabel(b),
                  }))}
                  placeholder="Select earlier build"
                  value={selectedBuildA || null}
                  onSelect={handleBuildAChange}
                  onClear={() => {
                    setSelectedBuildA('');
                    setBuildAObj(undefined);
                    setSelectedBuildB('');
                    setBuildBObj(undefined);
                    setBuildBOptions([]);
                  }}
                  showClear
                />
                <div className="flex h-10 items-center justify-center px-1" aria-hidden>
                  <ArrowRight className="h-[1.35rem] w-[1.35rem] shrink-0 text-[var(--inv-accent)]" />
                </div>
                <Combobox
                  id="build-b-select"
                  data={buildBOptions.map(b => ({
                    value: String(b.buildId),
                    label: formatBuildLabel(b),
                  }))}
                  placeholder="Select later build"
                  value={selectedBuildB || null}
                  onSelect={handleBuildBChange}
                  onClear={() => {
                    setSelectedBuildB('');
                    setBuildBObj(undefined);
                  }}
                  showClear
                  disabled={!selectedBuildA}
                />
              </div>

              <div className="flex flex-col gap-4 min-[769px]:hidden">
                <div className="min-w-0">
                  <label className={formLabelClass} htmlFor="build-a-select-mobile">
                    Build A (from)
                  </label>
                  <Combobox
                    id="build-a-select-mobile"
                    data={buildList.map(b => ({
                      value: String(b.buildId),
                      label: formatBuildLabel(b),
                    }))}
                    placeholder="Select earlier build"
                    value={selectedBuildA || null}
                    onSelect={handleBuildAChange}
                    onClear={() => {
                      setSelectedBuildA('');
                      setBuildAObj(undefined);
                      setSelectedBuildB('');
                      setBuildBObj(undefined);
                      setBuildBOptions([]);
                    }}
                    showClear
                  />
                </div>
                <div className="flex justify-center py-1" aria-hidden>
                  <ArrowRight className="h-[1.35rem] w-[1.35rem] rotate-90 text-[var(--inv-accent)]" />
                </div>
                <div className="min-w-0">
                  <label className={formLabelClass} htmlFor="build-b-select-mobile">
                    Build B (to)
                  </label>
                  <Combobox
                    id="build-b-select-mobile"
                    data={buildBOptions.map(b => ({
                      value: String(b.buildId),
                      label: formatBuildLabel(b),
                    }))}
                    placeholder="Select later build"
                    value={selectedBuildB || null}
                    onSelect={handleBuildBChange}
                    onClear={() => {
                      setSelectedBuildB('');
                      setBuildBObj(undefined);
                    }}
                    showClear
                    disabled={!selectedBuildA}
                  />
                </div>
              </div>

              {(selectedBuildA && buildAObj) ||
              (selectedBuildB && buildBObj) ? (
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-5 gap-y-4 max-[768px]:grid-cols-1">
                  <div className="min-w-0">
                    {selectedBuildA && buildAObj ? (
                      <BuildDetailsPanel build={buildAObj} />
                    ) : null}
                  </div>
                  <span className="max-[768px]:hidden" aria-hidden />
                  <div className="min-w-0">
                    {selectedBuildB && buildBObj ? (
                      <BuildDetailsPanel build={buildBObj} />
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="hidden grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-5 gap-y-2 min-[769px]:grid">
                <label className={formLabelClass} htmlFor="date-a">
                  Start date
                </label>
                <span aria-hidden />
                <label className={formLabelClass} htmlFor="date-b">
                  End date
                </label>
                <Input
                  id="date-a"
                  type="date"
                  value={dateA}
                  min="2025-01-01"
                  max={TODAY.slice(0, 10)}
                  onChange={e => setDateA(e.target.value)}
                />
                <div className="flex h-10 items-center justify-center px-1" aria-hidden>
                  <ArrowRight className="h-[1.35rem] w-[1.35rem] shrink-0 text-[var(--inv-accent)]" />
                </div>
                <Input
                  id="date-b"
                  type="date"
                  value={dateB}
                  min={dateA || '2025-01-01'}
                  max={TODAY.slice(0, 10)}
                  onChange={e => setDateB(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-4 min-[769px]:hidden">
                <div className="min-w-0">
                  <label className={formLabelClass} htmlFor="date-a-mobile">
                    Start date
                  </label>
                  <Input
                    id="date-a-mobile"
                    type="date"
                    value={dateA}
                    min="2025-01-01"
                    max={TODAY.slice(0, 10)}
                    onChange={e => setDateA(e.target.value)}
                  />
                </div>
                <div className="flex justify-center py-1" aria-hidden>
                  <ArrowRight className="h-[1.35rem] w-[1.35rem] rotate-90 text-[var(--inv-accent)]" />
                </div>
                <div className="min-w-0">
                  <label className={formLabelClass} htmlFor="date-b-mobile">
                    End date
                  </label>
                  <Input
                    id="date-b-mobile"
                    type="date"
                    value={dateB}
                    min={dateA || '2025-01-01'}
                    max={TODAY.slice(0, 10)}
                    onChange={e => setDateB(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-end border-t border-[var(--inv-border-soft)] pt-4">
            <Button
              onClick={handleGenerateReport}
              disabled={
                isComparing ||
                (!dateSwitch
                  ? !selectedBuildA || !selectedBuildB
                  : !dateA || !dateB)
              }>
              {isComparing ? 'Generating report…' : 'Generate comparison report'}
            </Button>
          </div>
        </section>

        {buildList.length > 0 ? (
          <section
            className={cn(
              buildDiffCardClass,
              'flex max-h-[min(22rem,38vh)] min-w-0 flex-col',
            )}
            aria-label="Repository activity">
            <BuildDiffCardHeader
              title="Activity timeline"
              subtitle={`${buildList.length.toLocaleString()} runs since onboarding`}
              action={<FilterPill>All activity</FilterPill>}
            />
            <ActivityTimeline events={timelineEvents} />
          </section>
        ) : null}
      </div>

      {buildList.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-[14px] border border-dashed border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-5 py-10 text-[0.875rem] text-[var(--inv-text-secondary)]">
          <BarChart3
            className="h-7 w-7 text-[var(--inv-accent)] opacity-70"
            aria-hidden
          />
          <p className="m-0">No builds found for this repository yet.</p>
        </div>
      ) : null}

      {diffData ? (
        <div className={cn(buildDiffCardClass, 'mb-5')}>
        <div
          className="grid grid-cols-1 gap-2 min-[640px]:grid-cols-3"
          role="tablist">
          {RESULT_TABS.map(tab => (
            <Button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selectTab === tab.id}
              variant={selectTab === tab.id ? 'default' : 'outline'}
              className={cn(
                'h-auto rounded-[10px] px-3.5 py-2.5 text-[0.8125rem] shadow-none hover:translate-y-0',
                selectTab !== tab.id &&
                  'bg-[var(--inv-surface-muted)] text-[var(--inv-text-secondary)] hover:bg-[var(--inv-accent-ring,rgba(0,70,184,0.06))] hover:text-[var(--inv-text)]',
              )}
              onClick={() => {
                if (tab.id === 'test-intelligence') {
                  setSelectTab('test-intelligence');
                  setShowMapTestCases(true);
                  return;
                }
                if (tab.id === 'test-optimization') {
                  handleTestOptimization();
                  return;
                }
                setSelectTab(tab.id);
              }}>
              {tab.label}
            </Button>
          ))}
        </div>
      </div>
    ) : null}

    {selectTab === 'code-diff' && diffData ? (
      <section className={cn(buildDiffCardClass, 'mb-5 min-w-0 overflow-x-auto')}>
        <h2 className="mb-5 flex min-w-0 flex-col gap-1 font-serif text-[1.0625rem] font-semibold text-[var(--inv-text)] sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-4 sm:gap-y-2">
          Comparison results
          <span className="min-w-0 break-words font-sans text-[0.8125rem] font-medium text-[var(--inv-text-secondary)]">
            Build {diffData.buildA} → Build {diffData.buildB}
          </span>
        </h2>
        <FileLevelChange
            diffData={diffData}
            onBoardedData={userSelectedComponent}
          />
        </section>
      ) : null}

      {selectTab === 'test-optimization' && diffData ? (
        <section className={cn(buildDiffCardClass, 'mb-5')}>
          {diffData?.scanB?.scanId?.trim() ||
          userSelectedComponent.scanId?.trim() ? (
            <TestOptimizationPanel
              embedded
              scanId={
                diffData?.scanB?.scanId?.trim() ||
                userSelectedComponent.scanId.trim()
              }
              codeRepositoryMongoId={userSelectedComponent.dbRepoId}
              repoUrl={userSelectedComponent.repoUrl}
              branchName={userSelectedComponent.branchName}
              patToken={userSelectedComponent.pat}
              diffData={diffData}
            />
          ) : (
            <p className="m-0 p-4 text-sm text-[var(--inv-text-secondary)]">
              Build B scan not resolved. Ensure builds were processed into
              scanCoverageTimeline, then generate the comparison again.
            </p>
          )}
        </section>
      ) : null}

      {selectTab === 'test-intelligence' && diffData ? (
        <>
          <MapTestCasesModal
            isOpen={showMapTestCases}
            onClose={() => setShowMapTestCases(false)}
            org={userSelectedComponent.orgName}
            project={userSelectedComponent.projectName}
            thresholdValue={thresholdValue}
            onThresholdChange={setThresholdValue}
            mapTestPatToken={mapTestPatToken}
            provider={scmProvider}
            onRunMapping={handleMethodMappingView}
            onFetchJira={handleJiraMapping}
          />
          {showTestMethodMapping && ingestionId.length > 0 ? (
            <TestMappingView
              ingestionId={ingestionId}
              generatedId={generatedId}
              isMappingLoading={isMappingLoading}
              thresholdValue={thresholdValue}
              gapAnalysis={gapAnalysis}
              onGapAnalysisReceived={setGapAnalysis}
              componentName={userSelectedComponent.repoName}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ComponentBuildDiff;
